import type { GeneratedForm, Generator } from "../../../../../Generator/src";
import type { SugarContent } from "../../../../../Generator/src/common/sugarContent";
import type { AdditionalContent, GenerationOptions } from "../../../../../Generator/src/generators/ProcessFormSources";
import type { FormSource } from "../../../../../Generator/src/generators/ReadFormSources";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequireResult = any;
const isEsModule = (obj: RequireResult): boolean => Boolean(obj && obj.__esModule);
const interopRequireDefault = (obj: RequireResult): RequireResult => (isEsModule(obj) ? obj : { default: obj });

type LocalizationEntryPoint = Pick<Generator, "getLocalizedSugar" | "getDictionary">;

export const importGenerator = (path: string): Generator => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const generator = interopRequireDefault(require(path));
    return adoptLegacyGenerator(generator);
};

// eslint-disable-next-line no-warning-comments
// TODO(01-06-2027) -- избавиться от этой обертки, импортировать движок напрямую
/**
 * Существует для сохранения обратной совместимости между Tools и движками:
 * - `<= 6.25.0` экспортирующими `getDictionary` и `getLocalizedSugar` единым объектом `localization`
 * - `<= 6.3?.0` функция `readSources` принимает 2 аргумента: `formPath: string`, `farmDirectory: string`
 */
const adoptLegacyGenerator = (generator: RequireResult): Generator => {
    const legacyLocalization = generator.localization as LocalizationEntryPoint;

    const adoptedReadSources: Generator["readSources"] = task => {
        const newGenerator = (generator.readSources as Function).length === 1;
        if (newGenerator) {
            const readSources: Generator["readSources"] = generator.readSources;
            return readSources(task);
        } else {
            const readSources: Generator_v6_25["readSources"] = generator.readSources;
            return readSources(task.formPath, task.farmDirectory);
        }
    };

    const adoptedProcessSugar: Generator["processSugar"] = generator.processSugar;

    const generatePolyfill: Generator["generate"] = async task => {
        const { sugarContent, resources } = await adoptedReadSources(task);
        return adoptedProcessSugar(sugarContent, resources, { resourcesHash: task.resourcesHash });
    };

    return {
        generate: generator.generate ?? generatePolyfill,
        processSugar: adoptedProcessSugar,
        readSources: adoptedReadSources,
        getAllDependenciesHash: generator.getAllDependenciesHash,
        getDictionary: generator.getDictionary ?? legacyLocalization.getDictionary,
        getLocalizedSugar: generator.getLocalizedSugar ?? legacyLocalization.getLocalizedSugar,
    };
};

interface Generator_v6_25 {
    readSources: (formSourcesPath: string, farmDirectory: string) => Promise<FormSource>;
    processSugar: (
        formSugarContent: SugarContent,
        additionalContent: AdditionalContent,
        generationOptions: null | undefined | GenerationOptions
    ) => GeneratedForm;
    localization: {
        getLocalizedSugar: (sugarFiles: SugarContent) => Promise<SugarContent>;
        getDictionary: (sugarFiles: SugarContent) => Promise<{ [relativeFileName: string]: string }>;
    };
    getAllDependencies: (formSourcesPath: string, farmDirectory: string) => Promise<string[]>;
    getAllDependenciesHash: (formSourcesPath: string, farmDirectory: string) => Promise<string>;
    getSugarTexts: (formSourcesPath: string, farmDirectory: string) => Promise<Record<string, string[] | null>>;
}
